import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { COLORS, FONTS } from '../constants/theme';

const INVITE_CODE = 'maxbobalot';
const LOCKOUT_DURATION = 30000; // 30 seconds in ms
const STORAGE_KEY_VERIFIED = 'miniwarriors_invite_verified';
const STORAGE_KEY_LOCKOUT = 'miniwarriors_invite_lockout';

export interface InviteGateOptions {
  width: number;
  height: number;
  onVerified: () => void;
}

export class InviteGate {
  public container: Container;
  private options: InviteGateOptions;
  private inputElement: HTMLInputElement | null = null;
  private inputContainer: HTMLDivElement | null = null;
  private errorText: Text | null = null;
  private submitButton: Container | null = null;
  private countdownText: Text | null = null;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private isLocked = false;

  constructor(options: InviteGateOptions) {
    this.options = options;
    this.container = new Container();
    this.checkLockoutState();
    this.create();
  }

  /**
   * Check if user is already verified
   */
  static isVerified(): boolean {
    return localStorage.getItem(STORAGE_KEY_VERIFIED) === 'true';
  }

  private checkLockoutState(): void {
    const lockoutEnd = localStorage.getItem(STORAGE_KEY_LOCKOUT);
    if (lockoutEnd) {
      const endTime = parseInt(lockoutEnd, 10);
      if (Date.now() < endTime) {
        this.isLocked = true;
      } else {
        // Lockout expired, clear it
        localStorage.removeItem(STORAGE_KEY_LOCKOUT);
      }
    }
  }

  private create(): void {
    const { width, height } = this.options;

    // Background overlay
    const bg = new Graphics();
    bg.rect(0, 0, width, height);
    bg.fill({ color: COLORS.BG_PRIMARY });
    this.container.addChild(bg);

    // Title
    const titleStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: 36,
      fontWeight: 'bold',
      fill: COLORS.PRIMARY,
    });
    const title = new Text({ text: 'Mini Warriors', style: titleStyle });
    title.anchor.set(0.5);
    title.x = width / 2;
    title.y = height / 2 - 120;
    this.container.addChild(title);

    // Subtitle
    const subtitleStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: 18,
      fill: COLORS.TEXT_SECONDARY,
    });
    const subtitle = new Text({ text: 'Enter invite code to continue', style: subtitleStyle });
    subtitle.anchor.set(0.5);
    subtitle.x = width / 2;
    subtitle.y = height / 2 - 70;
    this.container.addChild(subtitle);

    // Create HTML input for code entry
    this.createInput();

    // Error text (hidden initially)
    const errorStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: 14,
      fill: COLORS.DANGER,
    });
    this.errorText = new Text({ text: '', style: errorStyle });
    this.errorText.anchor.set(0.5);
    this.errorText.x = width / 2;
    this.errorText.y = height / 2 + 10;
    this.container.addChild(this.errorText);

    // Countdown text (for lockout)
    const countdownStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: 16,
      fill: COLORS.WARNING,
    });
    this.countdownText = new Text({ text: '', style: countdownStyle });
    this.countdownText.anchor.set(0.5);
    this.countdownText.x = width / 2;
    this.countdownText.y = height / 2 + 35;
    this.container.addChild(this.countdownText);

    // Submit button
    this.createSubmitButton();

    // If locked, start countdown
    if (this.isLocked) {
      this.startCountdown();
    }
  }

  private createInput(): void {
    const { width, height } = this.options;

    // Create container div for input
    this.inputContainer = document.createElement('div');
    this.inputContainer.style.cssText = `
      position: fixed;
      left: ${width / 2 - 120}px;
      top: ${height / 2 - 25}px;
      width: 240px;
      z-index: 1000;
    `;

    // Create input element
    this.inputElement = document.createElement('input');
    this.inputElement.type = 'text';
    this.inputElement.placeholder = 'Invite code';
    this.inputElement.autocomplete = 'off';
    this.inputElement.style.cssText = `
      width: 100%;
      padding: 12px 16px;
      font-size: 16px;
      font-family: ${FONTS.PRIMARY};
      background: ${this.hexToRgba(COLORS.BG_SECONDARY, 1)};
      border: 2px solid ${this.hexToRgba(COLORS.BORDER, 1)};
      border-radius: 8px;
      color: ${this.hexToRgba(COLORS.TEXT_PRIMARY, 1)};
      text-align: center;
      outline: none;
      box-sizing: border-box;
    `;

    this.inputElement.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        this.handleSubmit();
      }
    });

    this.inputContainer.appendChild(this.inputElement);
    document.body.appendChild(this.inputContainer);

    // Disable input if locked
    if (this.isLocked) {
      this.inputElement.disabled = true;
      this.inputElement.style.opacity = '0.5';
    }
  }

  private createSubmitButton(): void {
    const { width, height } = this.options;

    this.submitButton = new Container();
    this.submitButton.x = width / 2;
    this.submitButton.y = height / 2 + 80;
    this.submitButton.eventMode = 'static';
    this.submitButton.cursor = 'pointer';

    const buttonBg = new Graphics();
    buttonBg.roundRect(-80, -20, 160, 40, 8);
    buttonBg.fill({ color: this.isLocked ? COLORS.BG_SECONDARY : COLORS.PRIMARY });
    this.submitButton.addChild(buttonBg);

    const buttonText = new Text({
      text: 'Enter',
      style: new TextStyle({
        fontFamily: FONTS.PRIMARY,
        fontSize: 16,
        fontWeight: 'bold',
        fill: this.isLocked ? COLORS.TEXT_SECONDARY : COLORS.TEXT_PRIMARY,
      }),
    });
    buttonText.anchor.set(0.5);
    this.submitButton.addChild(buttonText);

    if (!this.isLocked) {
      this.submitButton.on('pointerdown', () => this.handleSubmit());
      this.submitButton.on('pointerover', () => {
        buttonBg.tint = 0xcccccc;
      });
      this.submitButton.on('pointerout', () => {
        buttonBg.tint = 0xffffff;
      });
    }

    this.container.addChild(this.submitButton);
  }

  private handleSubmit(): void {
    if (this.isLocked || !this.inputElement) return;

    const enteredCode = this.inputElement.value.trim().toLowerCase();

    if (enteredCode === INVITE_CODE) {
      // Success! Store verified state and proceed
      localStorage.setItem(STORAGE_KEY_VERIFIED, 'true');
      this.cleanup();
      this.options.onVerified();
    } else {
      // Wrong code - start lockout
      this.showError('Incorrect code');
      this.startLockout();
    }
  }

  private showError(message: string): void {
    if (this.errorText) {
      this.errorText.text = message;
    }
  }

  private startLockout(): void {
    const lockoutEnd = Date.now() + LOCKOUT_DURATION;
    localStorage.setItem(STORAGE_KEY_LOCKOUT, lockoutEnd.toString());
    this.isLocked = true;

    // Disable input
    if (this.inputElement) {
      this.inputElement.disabled = true;
      this.inputElement.style.opacity = '0.5';
      this.inputElement.value = '';
    }

    // Disable button visually
    if (this.submitButton) {
      const buttonBg = this.submitButton.getChildAt(0) as Graphics;
      buttonBg.clear();
      buttonBg.roundRect(-80, -20, 160, 40, 8);
      buttonBg.fill({ color: COLORS.BG_SECONDARY });
      this.submitButton.eventMode = 'none';
      this.submitButton.cursor = 'default';
    }

    this.startCountdown();
  }

  private startCountdown(): void {
    const updateCountdown = () => {
      const lockoutEnd = localStorage.getItem(STORAGE_KEY_LOCKOUT);
      if (!lockoutEnd) {
        this.endLockout();
        return;
      }

      const endTime = parseInt(lockoutEnd, 10);
      const remaining = Math.max(0, endTime - Date.now());

      if (remaining <= 0) {
        this.endLockout();
        return;
      }

      const seconds = Math.ceil(remaining / 1000);
      if (this.countdownText) {
        this.countdownText.text = `Try again in ${seconds} second${seconds !== 1 ? 's' : ''}`;
      }
    };

    updateCountdown();
    this.countdownInterval = setInterval(updateCountdown, 100);
  }

  private endLockout(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }

    localStorage.removeItem(STORAGE_KEY_LOCKOUT);
    this.isLocked = false;

    // Clear texts
    if (this.countdownText) {
      this.countdownText.text = '';
    }
    if (this.errorText) {
      this.errorText.text = '';
    }

    // Re-enable input
    if (this.inputElement) {
      this.inputElement.disabled = false;
      this.inputElement.style.opacity = '1';
    }

    // Re-enable button
    if (this.submitButton) {
      const buttonBg = this.submitButton.getChildAt(0) as Graphics;
      buttonBg.clear();
      buttonBg.roundRect(-80, -20, 160, 40, 8);
      buttonBg.fill({ color: COLORS.PRIMARY });
      this.submitButton.eventMode = 'static';
      this.submitButton.cursor = 'pointer';
      this.submitButton.on('pointerdown', () => this.handleSubmit());
    }
  }

  private hexToRgba(hex: number, alpha: number): string {
    const r = (hex >> 16) & 255;
    const g = (hex >> 8) & 255;
    const b = hex & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  public resize(width: number, height: number): void {
    this.options.width = width;
    this.options.height = height;

    // Update input position
    if (this.inputContainer) {
      this.inputContainer.style.left = `${width / 2 - 120}px`;
      this.inputContainer.style.top = `${height / 2 - 25}px`;
    }

    // Rebuild the container
    this.container.removeChildren();

    // Background
    const bg = new Graphics();
    bg.rect(0, 0, width, height);
    bg.fill({ color: COLORS.BG_PRIMARY });
    this.container.addChild(bg);

    // Title
    const titleStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: 36,
      fontWeight: 'bold',
      fill: COLORS.PRIMARY,
    });
    const title = new Text({ text: 'Mini Warriors', style: titleStyle });
    title.anchor.set(0.5);
    title.x = width / 2;
    title.y = height / 2 - 120;
    this.container.addChild(title);

    // Subtitle
    const subtitleStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: 18,
      fill: COLORS.TEXT_SECONDARY,
    });
    const subtitle = new Text({ text: 'Enter invite code to continue', style: subtitleStyle });
    subtitle.anchor.set(0.5);
    subtitle.x = width / 2;
    subtitle.y = height / 2 - 70;
    this.container.addChild(subtitle);

    // Error text
    const errorStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: 14,
      fill: COLORS.DANGER,
    });
    const oldErrorText = this.errorText?.text || '';
    this.errorText = new Text({ text: oldErrorText, style: errorStyle });
    this.errorText.anchor.set(0.5);
    this.errorText.x = width / 2;
    this.errorText.y = height / 2 + 10;
    this.container.addChild(this.errorText);

    // Countdown text
    const countdownStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: 16,
      fill: COLORS.WARNING,
    });
    const oldCountdownText = this.countdownText?.text || '';
    this.countdownText = new Text({ text: oldCountdownText, style: countdownStyle });
    this.countdownText.anchor.set(0.5);
    this.countdownText.x = width / 2;
    this.countdownText.y = height / 2 + 35;
    this.container.addChild(this.countdownText);

    // Recreate submit button
    this.submitButton = new Container();
    this.submitButton.x = width / 2;
    this.submitButton.y = height / 2 + 80;
    this.submitButton.eventMode = this.isLocked ? 'none' : 'static';
    this.submitButton.cursor = this.isLocked ? 'default' : 'pointer';

    const buttonBg = new Graphics();
    buttonBg.roundRect(-80, -20, 160, 40, 8);
    buttonBg.fill({ color: this.isLocked ? COLORS.BG_SECONDARY : COLORS.PRIMARY });
    this.submitButton.addChild(buttonBg);

    const buttonText = new Text({
      text: 'Enter',
      style: new TextStyle({
        fontFamily: FONTS.PRIMARY,
        fontSize: 16,
        fontWeight: 'bold',
        fill: this.isLocked ? COLORS.TEXT_SECONDARY : COLORS.TEXT_PRIMARY,
      }),
    });
    buttonText.anchor.set(0.5);
    this.submitButton.addChild(buttonText);

    if (!this.isLocked) {
      this.submitButton.on('pointerdown', () => this.handleSubmit());
    }

    this.container.addChild(this.submitButton);
  }

  public cleanup(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }

    if (this.inputContainer && this.inputContainer.parentNode) {
      this.inputContainer.parentNode.removeChild(this.inputContainer);
    }
    this.inputElement = null;
    this.inputContainer = null;
  }

  public destroy(): void {
    this.cleanup();
    this.container.destroy({ children: true });
  }
}
